import ApiBase from '../apiBase';
import CacheService from '../../services/CacheService';
import { isAfterMeasuresValue } from '../../views/home/components/reportComponent/reportUtils';
import { loadDistrictBounds } from '../../utils/districtBoundsLoader';

/**
 * "Tümünü getir" sayfa boyutu.
 *
 * Events/Locations/ActorCategories/EventTypes uçları tek seferde tüm kayıtlarla
 * çekiliyor çünkü harita kümeleme (clustering) ve filtreleme aynı anda TÜM noktalara
 * ihtiyaç duyuyor; kısmi sayfalama haritayı bozar.
 *
 * Yanıt boyutu maliyeti backend response compression (gzip/brotli) ile büyük ölçüde
 * azaltıldı. Bir sonraki iyileştirme adımı — gerçek sayfalama yerine — /api/Events için
 * ALAN PROJEKSİYONU olmalı: harita yalnızca lat/long/eventTypeId/date/description
 * kullanıyor; tam event nesnesi yerine bu ince DTO döndürülürse payload ciddi küçülür.
 * Bu, backend GetListEvents sorgusunu ve tüketicileri birlikte değiştirmeyi gerektirir.
 */
const ALL_RECORDS_PAGE_SIZE = 9999999;
const pageSize = ALL_RECORDS_PAGE_SIZE;

// Cache anahtarları için sabitler
const CACHE_KEYS = {
	PINS: 'pins',
	LOCATIONS: 'locations',
	ACTOR_CATEGORIES: 'actorCategories',
	EVENT_TYPES: 'eventTypes',
	QUESTIONS: (categoryId?: string) => `questions_${categoryId || 'all'}`,
	QUESTION_CATEGORIES: 'questionCategories',
	PIN_INFO: (pinId: string) => `pin_info_${pinId}`,
	PIN_EVENTS: (pinId: string, startDate: string, endDate: string) =>
		`pin_events_${pinId}_${startDate}_${endDate}`,
	QUESTION_ANSWERS: (pinId: string) => `question_answers_${pinId}`,
	CATEGORY_RISK_ANSWERS: (pinId: string) => `category_risk_answers_${pinId}`,
	PIN_REPORT: (pinId: string) => `pin_report_${pinId}`,
	PIN_FULL_REPORT: (pinId: string) => `pin_full_report_${pinId}`,
	PIN_QUESTIONS_AND_ANSWERS: (pinId: string) => `pin_questions_and_answers_${pinId}`,
};

// Expiry sürelerini milisaniye cinsinden tanımla
const CACHE_EXPIRY = {
	SHORT: 5 * 60 * 1000, // 5 dakika
	MEDIUM: 30 * 60 * 1000, // 30 dakika
	LONG: 12 * 60 * 60 * 1000, // 12 saat
	VERY_LONG: 24 * 60 * 60 * 1000, // 24 saat
};

/** Aegis GET yanıtını isim listesine çevirir (düz dizi, { items }, { sectors } vb.). */
function parseAegisNameList(data: unknown): string[] {
	if (data == null) return [];
	if (Array.isArray(data)) {
		if (data.length === 0) return [];
		const first = data[0];
		if (typeof first === 'string') return data as string[];
		return (data as Record<string, unknown>[])
			.map((row) => {
				if (typeof row === 'string') return row;
				const o = row as { name?: string; Name?: string; title?: string; Title?: string };
				return String(o.name ?? o.Name ?? o.title ?? o.Title ?? '');
			})
			.filter((s) => s.length > 0);
	}
	if (typeof data === 'object') {
		const o = data as Record<string, unknown>;
		const nested =
			o.items ??
			o.Items ??
			o.data ??
			o.Data ??
			o.sectors ??
			o.Sectors ??
			o.facilityTypes ??
			o.FacilityTypes ??
			o.values ??
			o.Values;
		if (nested != null) return parseAegisNameList(nested);
	}
	return [];
}

export interface PinData {
	Name: string;
	Latitude: number;
	Longitude: number;
	RadiusInMeters: number;
	Sector: string;
	userId: string;
	organizationId: string;
	File?: File;
}

interface PinResponse {
	id: string;
	userId: string;
	organizationId: string;
	name: string;
	latitude: number;
	longitude: number;
	radiusInMeters: number;
	locationCount: number;
	sector: string;
	reportDescription: string | null;
	districtDistributionJson: string;
	fileAttachments?: { id: string; fileName: string }[];
}

interface PinQuestionAnswer {
	questionId: string;
	userId: string;
	yesNo: boolean;
	findings: string;
	pinId: string;
	proposedSolutions: string;
	description: string;
	/** @deprecated Yeni kayıtlarda findingsFiles / proposedSolutionsFiles kullanın */
	files?: File[];
	findingsFiles?: File[];
	proposedSolutionsFiles?: File[];
	fileAttachmentIdsToRemove?: string[];
}

interface QuestionCategoryCreate {
	name: string;
	userId: string;
	questionTypeId: string;
}

interface QuestionCreate {
	text: string;
	userId: string;
	questionCategoryId: string;
	pinId: string;
}

export interface PinInfo {
	Id: string;
	UserId: string;
	Name: string;
	Latitude: number;
	Longitude: number;
	RadiusInMeters: number;
	LocationCount: number;
	Sector: string;
	ReportDescription: string | null;
	DistrictDistributionJson: string;
	QuestionsJson: {
		wikipediaTitle: string;
		wikipediaSummary: string;
	};
	JsonContentStream: any;
	Locations: any[];
	fileAttachments?: Array<{
		id: string;
		fileName: string;
		createdDate?: string;
	}>;
}

interface PinEventsResponse {
	locations: {
		locationId: string;
		district1: string;
		events: {
			id: string;
			date: string;
			eventType: string;
			description: string;
		}[];
	}[];
}

interface CategoryRiskAnswer {
	questionCategoryId: string;
	pinId: string;
	userId: string;
	probabilityLevel: number;
	impactLevel: number;
	threatLevel: number;
	appliedMeasureCoefficient: number;
	residualRiskLevel: number;
	isAfterMeasures: boolean;
}

interface CategoryRiskAnswerUpdate extends CategoryRiskAnswer {
	id: string;
}

interface TranscriptionResponse {
	transcription: string;
}

interface PinReportResponse {
	pinId: string;
	reportDescription: string;
	filteredEventCount: number;
	filteredLocationCount: number;
}

export enum ApprovalStatus {
	Pending = 0,
	Approved = 1,
	Rejected = 2
}

export interface ReportApprovalDto {
	id: string;
	fileAttachmentId: string;
	userId: string;
	userFirstName: string;
	userLastName: string;
	isReportOwner: boolean;
	status: ApprovalStatus;
	statusText: string;
	note: string | null;
	approvalDate: string;
}

export interface PendingReportsSummaryDto {
	totalPendingReports: number;
	pendingCountByPinId: Record<string, number>;
}

export interface ReportWithApprovalsDto {
	fileAttachmentId: string;
	fileName: string;
	createdDate: string;
	/** Sunucu yoksa istemcide onay tarihlerinden türetilir */
	updatedDate?: string;
	overallStatus: 'approved' | 'rejected' | 'pending';
	createdByFirstName?: string;
	createdByLastName?: string;
	/** Tamamlanırken bu PDF ile eşleşen InspectionReport (checklist snapshot). */
	inspectionReportId?: string | null;
	approvals: ReportApprovalDto[];
}

export interface CreateApprovalRequest {
	fileAttachmentId: string;
	status: ApprovalStatus;
	note?: string;
}

export interface UpdateApprovalRequest {
	status: ApprovalStatus;
	note?: string;
}

export interface DeletedReportDto {
	fileAttachmentId: string;
	fileName: string;
	createdDate: string;
	deletedDate: string;
	deletedByFirstName: string;
	deletedByLastName: string;
	pinId: string | null;
	pinName: string;
}

interface PinCategoryRiskAnswer {
	id: string;
	probabilityLevel: number;
	impactLevel: number;
	threatLevel: number;
	appliedMeasureCoefficient: number;
	residualRiskLevel: number;
	isAfterMeasures: boolean;
	categoryName: string;
	questionTypeName: string;
}

interface PinQuestionsResponse {
	pin: {
		id: string;
		name: string;
		latitude: number;
		longitude: number;
		radiusInMeters: number;
		sector: string;
		reportDescription: string | null;
		reportLocationPng?: string | null; // Rapor için kaydedilmiş harita görüntüsü FileAttachment ID'si
		reportLocationSatellitePng?: string | null;
		fileAttachments: {
			id: string;
			fileName: string;
		}[];
		questionAnswers: {
			id: string;
			questionId: string;
			questionText: string;
			questionCategoryName: string;
			description: string | null;
			findings: string;
			proposedSolutions: string;
			fileAttachments: {
				id: string;
				fileName: string;
			}[];
		}[];
		categoryRiskAnswers: PinCategoryRiskAnswer[];
	}
}

interface PinFullReportData {
	pinInfo: {
		Name: string;
		Sector: string;
		Latitude: number;
		Longitude: number;
		RadiusInMeters: number;
		reportDescription: string | null;
		fileAttachments: {
			id: string;
			fileName: string;
		}[];
		Id: string;
		reportLocationPng?: string | null;
		reportLocationSatellitePng?: string | null;
	};
	questionAnswers: {
		answer: {
			id: string;
			questionId: string;
			questionText: string;
			yesNo?: boolean | null;
			findings: string;
			proposedSolutions: string;
			description: string | null;
			questionCategoryName: string;
			fileAttachments: {
				id: string;
				fileName: string;
			}[];
		};
	}[];
	riskAnswers: {
		id: string;
		probabilityLevel: number;
		impactLevel: number;
		threatLevel: number;
		appliedMeasureCoefficient: number;
		residualRiskLevel: number;
		isAfterMeasures: boolean;
		categoryName: string;
		questionTypeName: string;
	}[];
}

type PinsResponseType = {
	items: PinResponse[];
	count: number;
	index: number;
	size: number;
	hasNext: boolean;
	hasPrevious: boolean;
};

interface ChangePasswordRequest {
	email: string;
	oldPassword: string;
	newPassword: string;
}

interface VerifyCodeRequest {
	email: string;
	code: string;
	validationType: number;
}

interface VerifyCodeResponse {
	success: boolean;
	isSuccess: boolean;
	isVerified: boolean;
	message?: string;
}

export interface UserResponse {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	status: boolean;
	emailVerified: boolean;
	organizationId: string;
	operationClaimIds: string[];
}

export interface GetListResponse<T> {
	items: T[];
	index: number;
	size: number;
	count: number;
	pages: number;
	hasNext: boolean;
	hasPrevious: boolean;
}

interface QuestionType {
	id: string;
	name: string;
}

interface QuestionCategory {
	id: string;
	name: string;
	isDisabled: boolean;
	index: number;
	questionType: QuestionType;
}

export interface PinQuestionDetailsAnswer {
	id: string;
	yesNo: boolean;
	description: string | null;
	findings: string;
	proposedSolutions: string;
	fileAttachments: {
		id: string;
		fileName: string;
	}[];
	pinId: string;
}

export interface PinQuestion {
	id: string;
	text: string;
	isDisabled: boolean;
	userId: string | null;
	index: number;
	questionCategory: QuestionCategory;
	answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsAndAnswersResponse {
	questions: PinQuestion[];
}

/** PUT /api/Pins yanıtı (dosya yüklendikten sonra yeni ekler dahil tüm pin dosyaları). */
export interface UpdatedPinFileAttachmentDto {
	id: string;
	fileName: string;
	/** Sunucu FileAttachment.CreatedDate (aynı dosya adıyla birden fazla kayıtta en yenisini seçmek için). */
	createdDate?: string;
}

export interface UpdatedPinResponseDto {
	id: string;
	fileAttachments: UpdatedPinFileAttachmentDto[];
}

interface SendEmailValidationRequest {
	email: string;
	force?: boolean;
}

interface SendEmailValidationResponse {
	success: boolean;
	message?: string;
}

interface GenerateInsightRequest {
	question: string;
	tespit: string;
	promptType?: 'finding' | 'evaluation';
	category?: string;
	facilityType?: string;
	facilityContext?: string;
	findingSeverity?: string;
	answerValue?: string;
	overallRiskContext?: string;
}

interface GenerateInsightResponse {
	insight: string;
}

const getPinReport = async (pinId: string, startDate: string, endDate: string): Promise<PinReportResponse> => {
	// NOT cached: rapor üretimi GPT'ye gidiyor; cache'lemek (a) tarihi anahtarda taşımadığımız için
	// farklı tarih aralıklarını birbirine karıştırır, (b) başarısız/null cevapları 30 dk boyunca
	// kullanıcıya tekrar tekrar gösterir. Her tetikleyici çağrı yeniden API'ye gitsin.
	CacheService.clear(CACHE_KEYS.PIN_REPORT(pinId));
	return ApiBase.postReportPromise<PinReportResponse>(
		`/api/Pins/${pinId}/createReport?startDate=${startDate}&endDate=${endDate}`
	);
};

const getPinFullReport = async (pinId: string): Promise<PinFullReportData> => {
	const cacheKey = CACHE_KEYS.PIN_FULL_REPORT(pinId);

	return CacheService.getOrFetch<PinFullReportData>(
		cacheKey,
		async () => {
			try {
				const response = (await ApiBase.getPromise<PinQuestionsResponse>(`/api/Pins/Questions?pinId=${pinId}`)) as any;
				const pin = response.pin ?? response.Pin;
				if (!pin) {
					throw new Error('Pin yanıtı beklenen formatta değil (pin/Pin yok).');
				}
				const cra = pin.categoryRiskAnswers ?? pin.CategoryRiskAnswers ?? [];

				return {
					pinInfo: {
						Name: pin.name ?? pin.Name,
						Sector: pin.sector ?? pin.Sector,
						Latitude: pin.latitude ?? pin.Latitude,
						Longitude: pin.longitude ?? pin.Longitude,
						RadiusInMeters: pin.radiusInMeters ?? pin.RadiusInMeters,
						reportDescription: pin.reportDescription ?? pin.ReportDescription,
						fileAttachments: pin.fileAttachments ?? pin.FileAttachments ?? [],
						Id: pin.id ?? pin.Id,
						reportLocationPng: pin.reportLocationPng ?? pin.ReportLocationPng ?? null,
						reportLocationSatellitePng: pin.reportLocationSatellitePng ?? pin.ReportLocationSatellitePng ?? null,
					},
					questionAnswers: (pin.questionAnswers ?? pin.QuestionAnswers ?? []).map((qa: any) => ({
						answer: {
							id: qa.id ?? qa.Id,
							questionId: qa.questionId ?? qa.QuestionId,
							questionText: qa.questionText ?? qa.QuestionText,
							yesNo: qa.yesNo ?? qa.YesNo,
							findings: qa.findings ?? qa.Findings,
							proposedSolutions: qa.proposedSolutions ?? qa.ProposedSolutions,
							description: qa.description ?? qa.Description,
							questionCategoryName: qa.questionCategoryName ?? qa.QuestionCategoryName,
							fileAttachments: qa.fileAttachments ?? qa.FileAttachments ?? []
						}
					})),
					riskAnswers: (Array.isArray(cra) ? cra : []).map((ra: any) => ({
						id: String(ra.id ?? ra.Id),
						probabilityLevel: Number(ra.probabilityLevel ?? ra.ProbabilityLevel ?? 0),
						impactLevel: Number(ra.impactLevel ?? ra.ImpactLevel ?? 0),
						threatLevel: Number(ra.threatLevel ?? ra.ThreatLevel ?? 0),
						appliedMeasureCoefficient: Number(ra.appliedMeasureCoefficient ?? ra.AppliedMeasureCoefficient ?? 0),
						residualRiskLevel: Number(ra.residualRiskLevel ?? ra.ResidualRiskLevel ?? 0),
						isAfterMeasures: isAfterMeasuresValue(ra.isAfterMeasures ?? ra.IsAfterMeasures),
						categoryName: String(ra.categoryName ?? ra.CategoryName ?? ''),
						questionTypeName: String(ra.questionTypeName ?? ra.QuestionTypeName ?? '')
					}))
				};
			} catch (error) {
				console.error('getPinFullReport error:', error);
				throw error;
			}
		},
		CACHE_EXPIRY.MEDIUM
	);
};

const getFileAttachment = async (fileAttachmentId: string): Promise<Blob> => {
	try {
		return await ApiBase.getBlobPromise(
			`/api/FileAttachments/${fileAttachmentId}/download`
		);
	} catch (error) {
		throw error;
	}
};

const getPinQuestionsAndAnswers = async (pinId: string): Promise<PinQuestionsAndAnswersResponse> => {
	try {
		const response = await ApiBase.getPromise<PinQuestionsAndAnswersResponse>(
			`/api/Pins/questions/details?pinId=${pinId}`
		);
		return response as PinQuestionsAndAnswersResponse;
	} catch (error) {
		console.error('getPinQuestionsAndAnswers error:', error);
		throw error;
	}
};

const getReportApprovalsByPin = async (pinId: string): Promise<ReportWithApprovalsDto[]> => {
	try {
		const response = await ApiBase.getPromise<ReportWithApprovalsDto[]>(`/api/ReportApprovals/by-pin/${pinId}`);
		return response as ReportWithApprovalsDto[];
	} catch (error) {
		console.error('getReportApprovalsByPin error:', error);
		throw error;
	}
};

const getPendingReportsSummary = async (organizationId: string): Promise<PendingReportsSummaryDto> => {
	const response = await ApiBase.getPromise<PendingReportsSummaryDto>(
		`/api/ReportApprovals/pending-summary?organizationId=${encodeURIComponent(organizationId)}`,
	);
	return response as PendingReportsSummaryDto;
};

const createReportApproval = async (data: CreateApprovalRequest) => {
	return ApiBase.postJsonPromise('/api/ReportApprovals', data);
};

const updateReportApproval = async (id: string, data: UpdateApprovalRequest) => {
	return ApiBase.putCategoryRiskAnswerPromise(`/api/ReportApprovals/${id}`, data);
};

const deleteReportApproval = async (id: string) => {
	return ApiBase.deletePromise(`/api/ReportApprovals/${id}`, {});
};

const softDeleteReport = async (fileAttachmentId: string) => {
	return ApiBase.deletePromise(`/api/ReportApprovals/soft-delete/${fileAttachmentId}`, {});
};

const getDeletedReports = async (pinId?: string): Promise<DeletedReportDto[]> => {
	const query = pinId ? `?pinId=${pinId}` : '';
	return ApiBase.getPromise<DeletedReportDto[]>(`/api/ReportApprovals/deleted-reports${query}`) as Promise<DeletedReportDto[]>;
};

const restoreReport = async (fileAttachmentId: string) => {
	return ApiBase.postJsonPromise(`/api/ReportApprovals/restore/${fileAttachmentId}`, {});
};


const EventsApi = {
	// Tüm önbelleği temizle
	clearCache() {
		CacheService.clearAll();
	},

	// Belirli bir öğenin önbelleğini temizle
	clearCacheItem(key: string) {
		CacheService.clear(key);
	},

	getEvents(params?: any) {
		// Events sürekli değiştiği için önbelleğe almıyoruz
		return ApiBase.getPromise('/api/Events', { ...params, pageSize });
	},

	getEventsCounts(params?: any) {
		return ApiBase.getPromise('/api/Events', { ...params });
	},

	getPins() {
		const userId = localStorage.getItem('userId');
		const cacheKey = CACHE_KEYS.PINS;

		return CacheService.getOrFetch<PinsResponseType>(
			cacheKey,
			async () => {
				// First get the user to get their organizationId
				const user = await this.getUser();
				const response = await ApiBase.getPromise<PinsResponseType>(`/api/Pins?organizationId=${user.organizationId}`);
				return response as PinsResponseType;
			},
			CACHE_EXPIRY.MEDIUM
		);
	},

	getLocations() {
		return CacheService.getOrFetch(
			CACHE_KEYS.LOCATIONS,
			() => ApiBase.getPromise('/api/Locations', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	getActorCategories() {
		return CacheService.getOrFetch(
			CACHE_KEYS.ACTOR_CATEGORIES,
			() => ApiBase.getPromise('/api/ActorCategories', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	getEventTypes() {
		return CacheService.getOrFetch(
			CACHE_KEYS.EVENT_TYPES,
			() => ApiBase.getPromise('/api/EventTypes', { pageSize }),
			CACHE_EXPIRY.LONG
		);
	},

	createNewPin(pinData: PinData | FormData): Promise<PinResponse> {
		return ApiBase.postPromise<PinResponse>('/api/Pins/UploadQuestionsJson', pinData)
			.then(response => {
				// Pin oluşturulduğunda pin önbelleğini temizle
				CacheService.clear(CACHE_KEYS.PINS);
				return response;
			});
	},

	deletePin(pinId: string) {
		return ApiBase.deletePromise(`/api/Pins/${pinId}`, null)
			.then(response => {
				// Pin silindiğinde pin önbelleğini ve pin ile ilgili diğer önbellekleri temizle
				CacheService.clear(CACHE_KEYS.PINS);
				CacheService.clear(CACHE_KEYS.PIN_INFO(pinId));
				CacheService.clearByPrefix(`pin_events_${pinId}`);
				CacheService.clear(CACHE_KEYS.QUESTION_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.PIN_REPORT(pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				return response;
			});
	},

	deleteFileAttachment(fileAttachmentId: string) {
		return ApiBase.deletePromise(`/api/FileAttachments/${fileAttachmentId}`, null);
	},

	resetAuditData(pinId: string) {
		return ApiBase.deletePromise(`/api/Pins/${pinId}/audit-data`, null)
			.then(response => {
				CacheService.clear(CACHE_KEYS.QUESTION_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				CacheService.clear(CACHE_KEYS.PIN_INFO(pinId));
				return response;
			});
	},

	getDistrictBounds() {
		// 22 MB GeoJSON asla localStorage'a yazılmamalı (kota patlar). Modül seviyesinde
		// memoize eden loader kullanılır; veri in-memory tutulur, oturum başına tek indirme.
		return loadDistrictBounds();
	},

	saveQuestionAnswers(answer: PinQuestionAnswer) {
		const formData = new FormData();
		formData.append('QuestionId', answer.questionId || '');
		formData.append('UserId', answer.userId || '');
		formData.append('YesNo', answer.yesNo ? 'true' : 'false');
		formData.append('Findings', answer.findings || '');
		formData.append('PinId', answer.pinId || '');
		formData.append('ProposedSolutions', answer.proposedSolutions || '');
		formData.append('Description', answer.description || '');

		const hasSplit =
			(answer.findingsFiles && answer.findingsFiles.length > 0) ||
			(answer.proposedSolutionsFiles && answer.proposedSolutionsFiles.length > 0);
		if (hasSplit) {
			answer.findingsFiles?.forEach(file => formData.append('FindingsFiles', file));
			answer.proposedSolutionsFiles?.forEach(file => formData.append('ProposedSolutionsFiles', file));
		} else if (answer.files && answer.files.length > 0) {
			answer.files.forEach(file => {
				formData.append('Files', file);
			});
		}

		// Add files to remove
		if (answer.fileAttachmentIdsToRemove && answer.fileAttachmentIdsToRemove.length > 0) {
			answer.fileAttachmentIdsToRemove.forEach(fileId => {
				formData.append('FileAttachmentIdsToRemove', fileId);
			});
		}

		console.log('Saving question answer with files:', {
			newFiles: answer.files?.map(f => f.name) || [],
			filesToRemove: answer.fileAttachmentIdsToRemove || []
		});

		return ApiBase.postPromise('/api/QuestionAnswers', formData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(answer.pinId));
				return response;
			})
			.catch(error => {
				console.error('Error saving question answer:', error);
				// Cloud-specific error handling
				if (error.message?.includes('Network Error') || error.message?.includes('timeout')) {
					console.error('Network error - possible cloud connectivity issue');
				}
				throw error;
			});
	},

	saveQuestionCategory(categoryData: QuestionCategoryCreate) {
		return ApiBase.postQuestionPromise('/api/QuestionCategories', categoryData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(categoryData.questionTypeId));
				return response;
			});
	},

	deleteQuestionCategory(categoryId: string) {
		return ApiBase.deletePromise(`/api/QuestionCategories/${categoryId}`, null).then((response) => {
			// categories affect reports/questions views
			CacheService.clear(CACHE_KEYS.QUESTION_CATEGORIES);
			return response;
		});
	},

	saveQuestion(questionData: QuestionCreate) {
		return ApiBase.postQuestionPromise('/api/Questions', questionData)
			.then(response => {
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(questionData.pinId));
				return response;
			});
	},

	deleteQuestion(questionId: string) {
		return ApiBase.deletePromise(`/api/Questions/${questionId}`, null);
	},

	getPinInfo(pinId: string): Promise<PinInfo> {
		return CacheService.getOrFetch<PinInfo>(
			CACHE_KEYS.PIN_INFO(pinId),
			() => ApiBase.getPromise(`/api/Pins/${pinId}`) as Promise<PinInfo>,
			CACHE_EXPIRY.MEDIUM
		);
	},

	getPinEvents(pinId: string, startDate: string, endDate: string): Promise<PinEventsResponse> {
		const cacheKey = CACHE_KEYS.PIN_EVENTS(pinId, startDate, endDate);

		return CacheService.getOrFetch<PinEventsResponse>(
			cacheKey,
			() => ApiBase.getPromise(
				`/api/Pins/${pinId}/info?startDate=${startDate}&endDate=${endDate}`
			) as Promise<PinEventsResponse>,
			CACHE_EXPIRY.MEDIUM
		);
	},

	updateQuestionAnswer(formData: FormData) {
		return ApiBase.putPromise('/api/QuestionAnswers', formData)
			.then(response => {
				// Clear relevant caches after update
				const pinId = formData.get('PinId') as string;
				if (pinId) {
					CacheService.clear(CACHE_KEYS.QUESTION_ANSWERS(pinId));
					CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				}
				return response;
			})
			.catch(error => {
				console.error('Error updating question answer:', error);
				throw error;
			});
	},

	saveCategoryRiskAnswers(data: CategoryRiskAnswer) {
		// isAfterMeasures'ın boolean olduğundan emin ol - undefined/null durumunda false olarak ayarla
		const requestData = {
			...data,
			isAfterMeasures: data.isAfterMeasures ?? false
		};
		return ApiBase.postQuestionPromise('/api/CategoryRiskAnswers', requestData)
			.then(response => {
				// Risk cevabı kaydedildiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(data.pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(data.pinId));
				return response;
			});
	},

	updateCategoryRiskAnswers(data: CategoryRiskAnswerUpdate) {
		// isAfterMeasures'ın boolean olduğundan emin ol - undefined/null durumunda false olarak ayarla
		const requestData = {
			...data,
			isAfterMeasures: data.isAfterMeasures ?? false
		};
		return ApiBase.putCategoryRiskAnswerPromise('/api/CategoryRiskAnswers', requestData)
			.then(response => {
				// Risk cevabı güncellendiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(data.pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(data.pinId));
				return response;
			});
	},

	deleteCategoryRiskAnswer(id: string, pinId: string) {
		return ApiBase.deletePromise(`/api/CategoryRiskAnswers/${id}`, null).then((response) => {
			CacheService.clear(CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId));
			CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
			return response;
		});
	},

	getCategoryRiskAnswers(pinId: string) {
		return CacheService.getOrFetch(
			CACHE_KEYS.CATEGORY_RISK_ANSWERS(pinId),
			() => ApiBase.getPromise<GetListResponse<any>>(`/api/CategoryRiskAnswers`, {
				PageIndex: 0,
				PageSize: 500,
				pinId: pinId
			}).then(response => {
				// Response formatını kontrol et - GetListResponse veya direkt array olabilir
				if (Array.isArray(response)) {
					return response;
				}
				const listResponse = response as GetListResponse<any>;
				if (listResponse && listResponse.items && Array.isArray(listResponse.items)) {
					return listResponse.items;
				}
				return [];
			}),
			CACHE_EXPIRY.MEDIUM
		);
	},

	getSpeechToText(audioBlob: Blob) {
		const formData = new FormData();
		formData.append('audioFile', audioBlob, 'audio.webm');

		return ApiBase.postPromise<TranscriptionResponse>('/api/SpeechToText/transcribe', formData);
	},

	getPinReport,
	getPinFullReport,

	updatePin(pinId: string, formData: FormData): Promise<UpdatedPinResponseDto> {
		// Log the pinId and iterate over formData entries
		console.log('Updating Pin:', pinId);
		formData.forEach((value, key) => {
			console.log(`${key}: ${value}`);
		});
		return ApiBase.putPromise(`/api/Pins`, formData)
			.then(response => {
				// Pin güncellendiğinde ilgili önbellekleri temizle
				CacheService.clear(CACHE_KEYS.PINS);
				CacheService.clear(CACHE_KEYS.PIN_INFO(pinId));
				CacheService.clear(CACHE_KEYS.PIN_FULL_REPORT(pinId));
				CacheService.clear(CACHE_KEYS.PIN_REPORT(pinId));
				return response as UpdatedPinResponseDto;
			});
	},

	getFileAttachment,
	
	getReportApprovalsByPin,
	getPendingReportsSummary,
	createReportApproval,
	updateReportApproval,
	deleteReportApproval,
	softDeleteReport,
	getDeletedReports,
	restoreReport,

	changePassword(data: ChangePasswordRequest) {
		return ApiBase.postQuestionPromise('/api/Auth/change-password', data);
	},

	// Current user profile update (self-service)
	updateUserFromAuth(data: { firstName?: string; lastName?: string }) {
		return ApiBase.putCategoryRiskAnswerPromise('/api/Users/FromAuth', data).then((response) => {
			// refresh cached current user
			CacheService.clear('user_current');
			return response;
		});
	},

	verifyCode(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
		const verifyData = {
			...data,
			validationType: 2 // Sabit validation type
		};

		return ApiBase.postJsonPromise<VerifyCodeResponse>('/api/Auth/verify-code', verifyData);
	},

	getUser(): Promise<UserResponse> {
		const cacheKey = `user_current`;

		return CacheService.getOrFetch<UserResponse>(
			cacheKey,
			async () => {
				const response = await ApiBase.getPromise<UserResponse>(`/api/Users/GetFromAuth`);
				return response as UserResponse;
			},
			CACHE_EXPIRY.MEDIUM
		);
	},

	// Önbelleği zorla yenileme metodları
	refreshPins() {
		return CacheService.refresh(CACHE_KEYS.PINS, async () => {
			const user = await this.getUser();
			const response = await ApiBase.getPromise<PinsResponseType>(`/api/Pins?organizationId=${user.organizationId}`);
			return response as PinsResponseType;
		});
	},

	refreshPinFullReport(pinId: string) {
		return CacheService.refresh(CACHE_KEYS.PIN_FULL_REPORT(pinId), () => getPinFullReport(pinId));
	},

	refreshQuestionCategories() {
		return CacheService.refresh(CACHE_KEYS.QUESTION_CATEGORIES, () =>
			ApiBase.getPromise('/api/QuestionCategories', { pageSize: 500 })
		);
	},

	getPinQuestionsAndAnswers,

	sendPasswordReset(email: string) {
		const data = { email };
		return ApiBase.postJsonPromise('/api/Auth/send-password-reset', data);
	},

	resetPassword(data: { email: string; code: string; newPassword: string }) {
		return ApiBase.postJsonPromise('/api/Auth/reset-password', data);
	},

	sendEmailValidation(data: SendEmailValidationRequest): Promise<SendEmailValidationResponse> {
		return ApiBase.postJsonPromise<SendEmailValidationResponse>('/api/Auth/send-email-validation', data);
	},

	verifyEmailCode(data: VerifyCodeRequest): Promise<VerifyCodeResponse> {
		const verifyData = {
			...data,
			validationType: 2 // Email validation type
		};

		return ApiBase.postJsonPromise<VerifyCodeResponse>('/api/Auth/verify-code', verifyData);
	},

	generateInsight(data: GenerateInsightRequest): Promise<GenerateInsightResponse> {
		return ApiBase.postJsonPromise<GenerateInsightResponse>('/api/QuestionAnswers/generate-insight', data);
	},

	// Admin functions
	getUsers(
		pageIndex: number = 0,
		pageSize: number = 10,
		roleFilter?: 'All' | 'Admin' | 'User',
		search?: string
	): Promise<GetListResponse<UserResponse>> {
		const rf = roleFilter ? `&roleFilter=${encodeURIComponent(roleFilter)}` : '';
		const s = search && search.trim().length > 0 ? `&search=${encodeURIComponent(search.trim())}` : '';
		return ApiBase.getPromise<GetListResponse<UserResponse>>(
			`/api/Users?PageIndex=${pageIndex}&PageSize=${pageSize}${rf}${s}`
		) as Promise<GetListResponse<UserResponse>>;
	},

	// Organizations (Superadmin)
	getOrganizations(pageIndex: number = 0, pageSize: number = 1000): Promise<GetListResponse<any>> {
		return ApiBase.getPromise<GetListResponse<any>>(
			`/api/Organizations?PageIndex=${pageIndex}&PageSize=${pageSize}`
		) as Promise<GetListResponse<any>>;
	},

	createOrganization(data: { name: string }): Promise<any> {
		return ApiBase.postJsonPromise('/api/Organizations', data);
	},

	deleteOrganization(id: string): Promise<any> {
		return ApiBase.deletePromise(`/api/Organizations/${id}`, {});
	},

	registerUser(data: {
		email: string;
		firstName: string;
		lastName: string;
		organizationId: string;
		operationClaimId?: string;
	}): Promise<any> {
		// Admin/Superadmin user creation (secured) - creates user and assigns default role "User"
		return ApiBase.postJsonPromise('/api/Users', data);
	},

	deleteUser(userId: string): Promise<any> {
		return ApiBase.deletePromise('/api/Users', { id: userId });
	},

	updateUser(data: {
		id: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		password?: string;
		organizationId?: string;
	}): Promise<any> {
		return ApiBase.putCategoryRiskAnswerPromise('/api/Users', data);
	},

	// Superadmin-only: set role (Admin/User) for a user
	setUserRole(userId: string, operationClaimId: string): Promise<any> {
		return ApiBase.putCategoryRiskAnswerPromise(`/api/Users/${userId}/Role`, { operationClaimId });
	},

	/**
	 * Aegis: tesis türü listesi. GET /api/Aegis/facility-types — yanıt dizi veya { items } vb. olabilir.
	 */
	getAegisFacilityTypes(): Promise<string[]> {
		return ApiBase.getPromise<unknown>('/api/Aegis/facility-types').then(parseAegisNameList);
	},
};

export default EventsApi;
